// Wave 20.22 — Unified /dropped/:token route.
//
// Drop links now share one URL prefix (/dropped) regardless of
// whether they point at an activity share or a peak share. Since
// the two backend endpoints (/public/shared/:token and
// /public/peak/:token) key off distinct tables, we try shared
// first, fall back to peak on 404, then render the matching
// page's component in place.
//
// Legacy /shared/:token and /peak/:token routes still exist —
// any link already sent stays working.

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import SharedActivity from './SharedActivity';
import PeakDropPage from './PeakDropPage';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

type Kind = 'activity' | 'peak' | 'notfound';

export default function Dropped() {
  const { token } = useParams<{ token: string }>();
  const [kind, setKind] = useState<Kind | null>(null);

  useEffect(() => {
    if (!token) {
      setKind('notfound');
      return;
    }
    let cancelled = false;
    (async () => {
      // Try activity first — it's the more common shape. HEAD
      // avoids paying the full payload cost on the miss path.
      try {
        const a = await fetch(`${API}/public/shared/${token}`, { method: 'GET' });
        if (cancelled) return;
        if (a.ok) {
          setKind('activity');
          return;
        }
      } catch {
        // network error — try the other kind
      }
      try {
        const p = await fetch(`${API}/public/peak/${token}`, { method: 'GET' });
        if (cancelled) return;
        if (p.ok) {
          setKind('peak');
          return;
        }
      } catch {}
      if (!cancelled) setKind('notfound');
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token || kind === 'notfound') {
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Drop not found
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 1 }}>
            The link may be mistyped or the drop may have expired.
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  if (kind === null) {
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }

  // Delegate to the existing per-kind pages so all their layout,
  // interactions, and expired-state handling comes along free.
  return kind === 'peak' ? <PeakDropPage /> : <SharedActivity />;
}
